import * as React from "react";
import { ViewState } from "@/types/owner-edit";
import { useEffect } from "react";

interface LevelProgressProps {
  level: number;
  currentExp: number;
  expToCurrentLevel: number;
  expToNextLevel: number;
  remainingExp: number;
  requiredExp: number;
  viewState: ViewState;
}

export default function LevelProgress({
  level,
  currentExp,
  expToCurrentLevel,
  expToNextLevel,
  remainingExp,
  requiredExp,
  viewState,
}: LevelProgressProps) {
  const [animatedProgress, setAnimatedProgress] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);

  const totalExpForLevel = expToNextLevel - expToCurrentLevel;
  const progress = totalExpForLevel > 0 ? remainingExp / totalExpForLevel : 0;
  const EPSILON = 0.5;
  const shouldShow100Percent =
    totalExpForLevel > 0 && remainingExp >= totalExpForLevel - 1 - EPSILON;
  const targetProgressPercentage = shouldShow100Percent
    ? 100
    : Math.min(Math.max(progress * 100, 0), 100);

  useEffect(() => {
    if (!viewState.showOwnerView || !viewState.isOwnerView) {
      setAnimatedProgress(0);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    const duration = 1000;
    const startTime = Date.now();
    const startProgress = animatedProgress;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);

      const easeOutCubic = 1 - Math.pow(1 - progressRatio, 3);
      const currentProgress =
        startProgress +
        (targetProgressPercentage - startProgress) * easeOutCubic;

      setAnimatedProgress(currentProgress);

      if (progressRatio < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [
    targetProgressPercentage,
    viewState.showOwnerView,
    viewState.isOwnerView,
  ]);

  const radius = 20.5;
  const center = 20.5;
  const angle = (animatedProgress / 100) * 360;

  const angleInRadians = ((angle - 90) * Math.PI) / 180;
  const endX = center + radius * Math.cos(angleInRadians);
  const endY = center + radius * Math.sin(angleInRadians);

  const largeArcFlag = angle > 180 ? 1 : 0;

  const arcPath =
    angle > 0
      ? `M ${center} ${center - radius} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} L ${center} ${center} Z`
      : "";

  return (
    <svg
      width="41"
      height="41"
      viewBox="0 0 41 41"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="20.5" cy="20.5" r="20.5" fill="black" />
      {((viewState.showOwnerView && viewState.isOwnerView) || isAnimating) &&
        arcPath && <path d={arcPath} fill="#00FF44" />}
      <circle cx="20.5" cy="20.5" r="16.5" fill="#2B2B2B" />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize="16"
        fontWeight="bold"
        fontFamily="monospace"
      >
        {level}
      </text>
    </svg>
  );
}
